/**
 * Encrypted Storage Service — AES-GCM 256-bit at-rest Encryption
 *
 * Provides browser-native encryption for sensitive offline mutation queues
 * stored in localStorage/IndexedDB. Uses the Web Crypto API exclusively
 * (zero external dependencies) for AES-GCM 256-bit encryption.
 *
 * Key Derivation:
 *   A session-bound seed (derived from the user's JWT `sub` claim)
 *   is passed through PBKDF2 to derive a non-extractable CryptoKey.
 *   When the user logs out, the seed is cleared, making all previously
 *   encrypted data permanently unreadable.
 *
 * @security Keys are non-extractable — cannot be exported via JS.
 * @security IV is unique per encryption (12-byte random).
 * @security Fail-safe: decryption failure purges the entry, never crashes.
 */

import logger from '../services/loggerService';
import ConfigurationService from '../services/ConfigurationService';
import { cryptoStore } from '../stores/useCryptoStore';
import { AUTH_SESSION_KEYS, CRYPTO_CONFIG } from '../constants/securityConstants';
import { EncryptedBlob } from '../types/security.types';

// Re-export type for compatibility
export type { EncryptedBlob };

/**
 * Fills a typed array or ArrayBuffer with zeroes to erase sensitive data from memory.
 */
function zeroBuffer(buffer: Uint8Array | ArrayBuffer | null | undefined): void {
  if (!buffer) return;
  try {
    const view = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    view.fill(0);
  } catch {
    // Fail-safe
  }
}

/**
 * Derives a non-extractable AES-GCM CryptoKey from a plaintext seed and salt
 * using PBKDF2 with SHA-256.
 */
async function deriveKey(seed: string, salt: Uint8Array): Promise<CryptoKey> {
  const saltBase64 = bufferToBase64(salt.buffer as ArrayBuffer);
  const cachedKey = cryptoStore.getState().getCachedKey(saltBase64);
  if (cachedKey) {
    return cachedKey;
  }

  const encoder = new TextEncoder();
  const encodedSeed = encoder.encode(seed);
  
  let keyMaterial: CryptoKey;
  try {
    keyMaterial = await crypto.subtle.importKey(
      'raw',
      encodedSeed,
      'PBKDF2',
      false,
      ['deriveKey']
    );
  } finally {
    // Zero out the temporary encoded seed array to prevent it from lingering in JS heap
    zeroBuffer(encodedSeed);
  }

  // Retrieve configuration dynamically with safe defaults
  const securityConfig = ConfigurationService.getSecurityConfig();
  const configIterations = securityConfig?.pbkdf2Iterations || CRYPTO_CONFIG.PBKDF2_ITERATIONS;
  const configKeyLength = securityConfig?.aesKeyLengthBits || CRYPTO_CONFIG.AES_KEY_LENGTH_BITS;

  // Strict validation of cryptographic parameters
  const iterations = Math.max(configIterations, CRYPTO_CONFIG.MIN_PBKDF2_ITERATIONS);
  const keyLength = [128, 192, 256].includes(configKeyLength) ? configKeyLength : CRYPTO_CONFIG.AES_KEY_LENGTH_BITS;

  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: keyLength },
    false, // Non-extractable — cannot be exported via JS
    ['encrypt', 'decrypt']
  );

  cryptoStore.getState().cacheKey(saltBase64, derivedKey);
  return derivedKey;
}

/**
 * Converts an ArrayBuffer to a base64 string for JSON-safe storage.
 */
function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Converts a base64 string back to a Uint8Array.
 */
function base64ToBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Seeds the encryption engine with a session-bound value.
 * Call this immediately after successful authentication.
 *
 * @param userId - The authenticated user's unique identifier (JWT `sub`).
 */
export function seedEncryptionKey(userId: string): void {
  if (typeof window === 'undefined') return;

  const prefix = `${userId}:${navigator.userAgent.substring(0, 32)}:`;
  const currentSeed = cryptoStore.getState().seed;
  if (currentSeed && currentSeed.startsWith(prefix)) {
    // Already seeded for the same user in this session, keep the existing entropy to prevent decryption failures on refresh
    return;
  }

  // Generate 32 bytes of cryptographically secure random session entropy to bind to the seed
  const sessionEntropyArray = crypto.getRandomValues(new Uint8Array(32));
  const sessionEntropyHex = Array.from(sessionEntropyArray)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  // Combine userId, navigator userAgent, and the dynamic high-entropy session value
  const seed = `${prefix}${sessionEntropyHex}`;
  cryptoStore.getState().setSeed(seed);

  // Erase temporary entropy buffer
  zeroBuffer(sessionEntropyArray);
}

/**
 * Invalidates the encryption key by clearing the session seed.
 * All previously encrypted data becomes permanently unreadable.
 * Call this during logout.
 */
export function invalidateEncryptionKey(): void {
  cryptoStore.getState().clearSeed();
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(AUTH_SESSION_KEYS.ENCRYPTION_SEED);
}

/**
 * Retrieves the current session seed, or null if not seeded.
 */
function getSessionSeed(): string | null {
  return cryptoStore.getState().seed;
}

/**
 * Encrypts arbitrary data into an EncryptedBlob using AES-GCM 256-bit.
 *
 * @param data - The data to encrypt (will be JSON-serialized).
 * @returns An EncryptedBlob containing the ciphertext, IV, and salt.
 * @throws Error if no encryption seed is available (user not authenticated).
 */
export async function encryptPayload(data: unknown): Promise<EncryptedBlob> {
  const seed = getSessionSeed();
  if (!seed) {
    throw new Error('Encryption seed not available. User must be authenticated.');
  }

  const salt = crypto.getRandomValues(new Uint8Array(CRYPTO_CONFIG.SALT_BYTE_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(CRYPTO_CONFIG.IV_BYTE_LENGTH));
  const key = await deriveKey(seed, salt);

  const encoder = new TextEncoder();
  let plaintextStr: string | null = JSON.stringify(data);
  const plaintext = encoder.encode(plaintextStr);
  plaintextStr = null; // Dereference immediately to clear from memory heap

  let ciphertext: ArrayBuffer;
  try {
    ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      plaintext
    );
  } finally {
    // Zero out plaintext buffer immediately
    zeroBuffer(plaintext);
  }

  const result = {
    ciphertext: bufferToBase64(ciphertext),
    iv: bufferToBase64(iv.buffer as ArrayBuffer),
    salt: bufferToBase64(salt.buffer as ArrayBuffer),
    version: CRYPTO_CONFIG.BLOB_SCHEMA_VERSION,
  };

  // Zero out transient arrays
  zeroBuffer(salt);
  zeroBuffer(iv);

  return result;
}

/**
 * Decrypts an EncryptedBlob back to its original data.
 *
 * @param blob - The encrypted blob from storage.
 * @returns The decrypted data, or null if decryption fails (key invalidated, corrupt data).
 */
export async function decryptPayload(blob: EncryptedBlob): Promise<unknown | null> {
  let salt: Uint8Array | null = null;
  let iv: Uint8Array | null = null;
  let ciphertext: Uint8Array | null = null;
  try {
    const seed = getSessionSeed();
    if (!seed) {
      logger.warn('Decryption attempted without encryption seed — data is permanently inaccessible', {
        context: 'CryptoStorageService',
      });
      return null;
    }

    if (blob.version !== CRYPTO_CONFIG.BLOB_SCHEMA_VERSION) {
      logger.warn('EncryptedBlob version mismatch — cannot decrypt', {
        expected: CRYPTO_CONFIG.BLOB_SCHEMA_VERSION,
        received: blob.version,
        context: 'CryptoStorageService',
      });
      return null;
    }

    salt = base64ToBuffer(blob.salt);
    iv = base64ToBuffer(blob.iv);
    ciphertext = base64ToBuffer(blob.ciphertext);
    const key = await deriveKey(seed, salt);

    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource },
      key,
      ciphertext as unknown as BufferSource
    );

    const decoder = new TextDecoder();
    let decryptedText: string | null = decoder.decode(plaintext);
    const result = JSON.parse(decryptedText);
    decryptedText = null; // Dereference immediately to clear from memory heap

    // Zero out decrypted plaintext ArrayBuffer immediately
    zeroBuffer(new Uint8Array(plaintext));

    return result;
  } catch (error) {
    // Fail-safe: corrupted or key-mismatched blobs are silently discarded.
    // This prevents crash loops when an old session's data encounters a new key.
    logger.warn('Decryption failed — purging corrupt or inaccessible entry', {
      context: 'CryptoStorageService',
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  } finally {
    zeroBuffer(salt);
    zeroBuffer(iv);
    zeroBuffer(ciphertext);
  }
}
